import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import SectionInfoIcon from "./SectionInfoIcon";

const T = {
  primary: "#2457BF",
  primaryDark: "#1A42A0",
  soft: "#F1F4F9",
  softBlue: "#E8EEF8",
  border: "#DCE3EF",
  text: "#1A2332",
  muted: "#6B7A90",
  green: "#1B7F4E",
  red: "#C0392B",
  white: "#FFFFFF",
};

const NUM = { fontVariantNumeric: "tabular-nums" };

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const pctInRange = (value, low, high) => {
  const v = toNum(value);
  const lo = toNum(low);
  const hi = toNum(high);
  if (v === null || lo === null || hi === null || hi === lo) return 50;
  return Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100));
};

function MiniRange({ label, low, high, marker, lowLbl, highLbl, format, infoText }) {
  const left = pctInRange(marker, low, high);
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 6,
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: T.muted,
            letterSpacing: "0.04em",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          {label}
          <SectionInfoIcon text={infoText} size={11} />
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.primary, ...NUM }}>
          {format(marker)}
        </span>
      </div>
      <div
        style={{
          height: 5,
          borderRadius: 3,
          background: `linear-gradient(90deg, ${T.red}33, ${T.softBlue}, ${T.green}33)`,
          position: "relative",
          marginBottom: 4,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: -3,
            left: `calc(${left}% - 5px)`,
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: T.primary,
            border: `2px solid ${T.white}`,
            boxShadow: "0 0 0 1px rgba(36,87,191,0.25)",
          }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, ...NUM }}>
        <span style={{ color: T.red, fontWeight: 600 }}>
          {lowLbl} {format(low)}
        </span>
        <span style={{ color: T.green, fontWeight: 600 }}>
          {highLbl} {format(high)}
        </span>
      </div>
    </div>
  );
}

function DenseTable({ title, rows, infoText }) {
  return (
    <div
      style={{
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        overflow: "hidden",
        background: T.white,
        height: "100%",
      }}
    >
      <div
        style={{
          padding: "7px 10px",
          background: T.soft,
          borderBottom: `1px solid ${T.border}`,
          fontSize: 10,
          fontWeight: 700,
          color: T.primaryDark,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          display: "flex",
          alignItems: "center",
        }}
      >
        {title}
        <SectionInfoIcon text={infoText} size={11} />
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.k}
              style={{ background: i % 2 ? T.soft : T.white }}
            >
              <td
                style={{
                  padding: "6px 10px",
                  fontSize: 11,
                  color: T.muted,
                  fontWeight: 600,
                  borderBottom: i < rows.length - 1 ? `1px solid ${T.border}` : "none",
                  width: "48%",
                }}
              >
                {row.k}
              </td>
              <td
                style={{
                  padding: "6px 10px",
                  fontSize: 12,
                  fontWeight: 700,
                  color:
                    row.tone === "pos"
                      ? T.green
                      : row.tone === "neg"
                        ? T.red
                        : T.text,
                  textAlign: "right",
                  borderBottom: i < rows.length - 1 ? `1px solid ${T.border}` : "none",
                  ...NUM,
                }}
              >
                {row.v}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SessionLevelsBoard({ snapshot, formatCurrency }) {
  const open = toNum(snapshot.open);
  const high = toNum(snapshot.dayHigh);
  const low = toNum(snapshot.dayLow);
  const pClose = toNum(snapshot.previousClose);
  const ltp = toNum(snapshot.lastTradedPrice);
  const atp = toNum(snapshot.averageTradePrice);

  const values = [open, high, low, pClose, ltp, atp].filter((v) => v !== null);
  if (!values.length) {
    return (
      <div style={{ padding: 16, fontSize: 12, color: T.muted }}>No session data</div>
    );
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pad = span * 0.08;
  const scaleMin = min - pad;
  const scaleMax = max + pad;
  const scaleSpan = scaleMax - scaleMin || 1;

  const yPct = (v) => {
    if (v === null) return null;
    return ((scaleMax - v) / scaleSpan) * 100;
  };

  const isUp = ltp !== null && open !== null ? ltp >= open : true;
  const bodyTop = open !== null && ltp !== null ? Math.max(open, ltp) : high;
  const bodyBot = open !== null && ltp !== null ? Math.min(open, ltp) : low;
  const wickTop = high;
  const wickBot = low;

  const levels = [
    { key: "H", label: "High", value: high, color: T.green },
    { key: "O", label: "Open", value: open, color: T.muted },
    { key: "ATP", label: "ATP", value: atp, color: "#7C8DB5" },
    { key: "PC", label: "PClose", value: pClose, color: T.text },
    { key: "LTP", label: "LTP", value: ltp, color: T.primary },
    { key: "L", label: "Low", value: low, color: T.red },
  ].filter((l) => l.value !== null);

  // Sort high → low for the ladder list
  const ladder = [...levels].sort((a, b) => b.value - a.value);

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "stretch",
        height: "100%",
        minHeight: 0,
      }}
    >
      {/* Session candle */}
      <div
        style={{
          width: 44,
          flexShrink: 0,
          position: "relative",
          background: T.soft,
          borderRadius: 6,
          border: `1px solid ${T.border}`,
          alignSelf: "stretch",
        }}
      >
        {/* wick */}
        {wickTop !== null && wickBot !== null && (
          <div
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              top: `${yPct(wickTop)}%`,
              bottom: `${100 - yPct(wickBot)}%`,
              width: 2,
              background: isUp ? T.green : T.red,
              borderRadius: 1,
            }}
          />
        )}
        {/* body */}
        {bodyTop !== null && bodyBot !== null && (
          <div
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              top: `${yPct(bodyTop)}%`,
              bottom: `${100 - yPct(bodyBot)}%`,
              width: 18,
              minHeight: 4,
              background: isUp ? T.green : T.red,
              borderRadius: 2,
              opacity: 0.9,
            }}
          />
        )}
        {/* LTP tick */}
        {ltp !== null && (
          <div
            style={{
              position: "absolute",
              left: 3,
              right: 3,
              top: `calc(${yPct(ltp)}% - 1px)`,
              height: 2,
              background: T.primary,
              borderRadius: 1,
              zIndex: 2,
            }}
          />
        )}
      </div>

      {/* Price ladder — fills remaining height evenly */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: 4,
          height: "100%",
          padding: "2px 0",
        }}
      >
        {ladder.map((lvl) => (
          <div
            key={lvl.key}
            style={{
              display: "grid",
              gridTemplateColumns: "42px 1fr 72px",
              alignItems: "center",
              gap: 8,
              flex: 1,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: lvl.color,
              }}
            >
              {lvl.label}
            </span>
            <div
              style={{
                height: 5,
                borderRadius: 2,
                background: T.soft,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${pctInRange(lvl.value, scaleMin, scaleMax)}%`,
                  background: lvl.color,
                  opacity: 0.85,
                  borderRadius: 2,
                }}
              />
            </div>
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: T.text,
                textAlign: "right",
                ...NUM,
              }}
            >
              {formatCurrency(lvl.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChartTip({ active, payload, label, fmt }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: 6,
        padding: "5px 8px",
        fontSize: 11,
        fontWeight: 700,
        color: T.text,
        boxShadow: "0 4px 12px rgba(15,23,42,0.1)",
      }}
    >
      {label}: {fmt(payload[0].value)}
    </div>
  );
}

export default function StockPreviewMarketSnapshotPanel({
  marketSnapshot,
  loading = false,
  error = null,
  onRetry,
}) {
  const pressure = useMemo(
    () => marketSnapshot?.getBuySellPressure?.() || null,
    [marketSnapshot],
  );
  const buySellData = useMemo(
    () => marketSnapshot?.getBuySellChartData?.() || [],
    [marketSnapshot],
  );

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: T.muted, fontSize: 12, fontWeight: 600 }}>
        <div
          style={{
            width: 20,
            height: 20,
            margin: "0 auto 8px",
            borderRadius: "50%",
            border: `2px solid ${T.softBlue}`,
            borderTopColor: T.primary,
            animation: "smpSpin 0.7s linear infinite",
          }}
        />
        Loading snapshot…
        <style>{`@keyframes smpSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: T.red, marginBottom: 8 }}>{error}</div>
        {typeof onRetry === "function" && (
          <button
            type="button"
            onClick={onRetry}
            style={{
              border: "none",
              borderRadius: 6,
              padding: "6px 14px",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              background: T.primary,
              color: "#fff",
            }}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (!marketSnapshot) {
    return (
      <div style={{ padding: 16, fontSize: 12, color: T.muted }}>No market snapshot available.</div>
    );
  }

  const fc = (v) => marketSnapshot.formatCurrency(v);
  const fi = (v) => marketSnapshot.formatInteger(v);
  const isPos = marketSnapshot.getIsPositiveChange?.();
  const changePct = marketSnapshot.getChangePercent?.();
  const changeTone = isPos === null ? null : isPos ? "pos" : "neg";
  const buyPct = pressure?.buyPct ?? 50;
  const sellPct = pressure?.sellPct ?? 50;
  const isAngelSnapshot = marketSnapshot.broker === "angelone";
  const highExtremeShort = isAngelSnapshot ? "52WH" : "ATH";
  const lowExtremeShort = isAngelSnapshot ? "52WL" : "ATL";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Compact KPI row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 8,
          border: `1px solid ${T.border}`,
          borderRadius: 8,
          overflow: "hidden",
          background: T.white,
        }}
      >
        {[
          {
            l: "LTP",
            v: `₹${fc(marketSnapshot.lastTradedPrice)}`,
            s: `ATP ${fc(marketSnapshot.averageTradePrice)}`,
            tone: changeTone,
            info: "Last traded price with the session average trade price (ATP).",
          },
          {
            l: "Change",
            v:
              marketSnapshot.netChange == null
                ? "-"
                : `${isPos ? "+" : ""}${fc(marketSnapshot.netChange)}`,
            s: changePct ? `${isPos ? "+" : ""}${changePct}%` : "vs PClose",
            tone: changeTone,
            info: "Net change amount and percentage versus the previous close.",
          },
          {
            l: "Volume",
            v: fi(marketSnapshot.volume),
            s: `Last ${fi(marketSnapshot.lastQuantity)}`,
            info: "Total traded volume for the session and quantity of the most recent trade.",
          },
          {
            l: isAngelSnapshot ? "Symbol" : "Mkt Cap",
            v: isAngelSnapshot
              ? marketSnapshot.tradingSymbol || "-"
              : `${fc(marketSnapshot.marketCap)} Cr`,
            s: `OI ${fi(marketSnapshot.openInterest)}`,
            info: isAngelSnapshot
              ? "Trading symbol with open interest (OI) for derivatives."
              : "Market capitalization in crores with open interest (OI).",
          },
        ].map((kpi, i) => (
          <div
            key={kpi.l}
            style={{
              padding: "10px 12px",
              borderRight: i < 3 ? `1px solid ${T.border}` : "none",
              background: i % 2 ? T.soft : T.white,
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: T.muted,
                letterSpacing: "0.06em",
                marginBottom: 2,
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              {kpi.l}
              <SectionInfoIcon text={kpi.info} size={10} />
            </div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 800,
                color: kpi.tone === "pos" ? T.green : kpi.tone === "neg" ? T.red : T.text,
                lineHeight: 1.2,
                ...NUM,
              }}
            >
              {kpi.v}
            </div>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.muted, marginTop: 2, ...NUM }}>
              {kpi.s}
            </div>
          </div>
        ))}
      </div>

      {/* Ranges + pressure in one row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr",
          gap: 10,
        }}
        className="smp-mid-row"
      >
        <div
          style={{
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            padding: "10px 12px",
            background: T.white,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <MiniRange
            label="DAY RANGE"
            low={marketSnapshot.dayLow}
            high={marketSnapshot.dayHigh}
            marker={marketSnapshot.lastTradedPrice}
            lowLbl="L"
            highLbl="H"
            format={fc}
            infoText="Shows where the last traded price sits between today's session low and high."
          />
          <MiniRange
            label="CIRCUIT"
            low={marketSnapshot.lowerCircuitLimit}
            high={marketSnapshot.upperCircuitLimit}
            marker={marketSnapshot.lastTradedPrice}
            lowLbl="LC"
            highLbl="UC"
            format={fc}
            infoText="Upper and lower circuit limits compared with the current last traded price."
          />
          <MiniRange
            label={isAngelSnapshot ? "52-WEEK" : "ALL-TIME"}
            low={marketSnapshot.allTimeLow}
            high={marketSnapshot.allTimeHigh}
            marker={marketSnapshot.lastTradedPrice}
            lowLbl={lowExtremeShort}
            highLbl={highExtremeShort}
            format={fc}
            infoText={
              isAngelSnapshot
                ? "52-week high and low extremes compared with the current price."
                : "All-time high and low extremes compared with the current price."
            }
          />
        </div>

        <div
          style={{
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            padding: "10px 12px",
            background: T.white,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            minHeight: 0,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: T.primaryDark,
              letterSpacing: "0.05em",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            ORDER FLOW
            <SectionInfoIcon text="Buy versus sell pressure from aggregated market-depth quantities and a side-by-side volume chart." size={11} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", ...NUM }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.green }}>
              Buy {buyPct.toFixed(0)}%
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.red }}>
              Sell {sellPct.toFixed(0)}%
            </span>
          </div>
          <div
            style={{
              display: "flex",
              height: 8,
              borderRadius: 4,
              overflow: "hidden",
              background: T.soft,
            }}
          >
            <div style={{ width: `${buyPct}%`, background: T.green }} />
            <div style={{ width: `${sellPct}%`, background: T.red }} />
          </div>
          <div style={{ fontSize: 10, color: T.muted, fontWeight: 600, ...NUM }}>
            Buy {fi(pressure?.buy)} · Sell {fi(pressure?.sell)}
            {pressure?.ratio != null ? ` · Ratio ${pressure.ratio.toFixed(2)}` : ""}
          </div>
          <div style={{ flex: 1, minHeight: 110 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={buySellData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: T.muted }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 9, fill: T.muted }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) =>
                    Number(v).toLocaleString("en-IN", { notation: "compact", maximumFractionDigits: 1 })
                  }
                />
                <Tooltip content={<ChartTip fmt={fi} />} />
                <Bar dataKey="quantity" radius={[4, 4, 0, 0]} maxBarSize={40}>
                  {buySellData.map((e) => (
                    <Cell key={e.name} fill={e.name === "Buy" ? T.green : T.red} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts + tables */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1.15fr",
          gap: 10,
          alignItems: "stretch",
        }}
        className="smp-bottom-row"
      >
        <div
          style={{
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            padding: "8px 10px 10px",
            background: T.white,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            height: "100%",
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: T.primaryDark,
              letterSpacing: "0.05em",
              marginBottom: 8,
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            SESSION LEVELS
            <SectionInfoIcon text="Visual ladder of session open, high, low, previous close, average trade price, and last traded price." size={11} />
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <SessionLevelsBoard snapshot={marketSnapshot} formatCurrency={fc} />
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
          }}
        >
          <DenseTable
            title="Session"
            infoText="Today's open, high, low, previous close, and net change for the current session."
            rows={[
              { k: "Open", v: fc(marketSnapshot.open) },
              { k: "High", v: fc(marketSnapshot.dayHigh), tone: "pos" },
              { k: "Low", v: fc(marketSnapshot.dayLow), tone: "neg" },
              { k: "PClose", v: fc(marketSnapshot.previousClose) },
              {
                k: "Change",
                v:
                  marketSnapshot.netChange == null
                    ? "-"
                    : `${isPos ? "+" : ""}${fc(marketSnapshot.netChange)}`,
                tone: changeTone,
              },
            ]}
          />
          <DenseTable
            title="Limits & Cap"
            infoText={
              isAngelSnapshot
                ? "Circuit limits, 52-week high/low extremes, and trading symbol details."
                : "Circuit limits, all-time high/low extremes, and market capitalization."
            }
            rows={[
              { k: "UC", v: fc(marketSnapshot.upperCircuitLimit), tone: "pos" },
              { k: "LC", v: fc(marketSnapshot.lowerCircuitLimit), tone: "neg" },
              { k: highExtremeShort, v: fc(marketSnapshot.allTimeHigh) },
              { k: lowExtremeShort, v: fc(marketSnapshot.allTimeLow) },
              ...(isAngelSnapshot
                ? [
                    {
                      k: "Symbol",
                      v: marketSnapshot.tradingSymbol || "-",
                    },
                  ]
                : [
                    {
                      k: "Mkt Cap",
                      v: `${fc(marketSnapshot.marketCap)} Cr`,
                    },
                  ]),
            ]}
          />
          <DenseTable
            title="Liquidity"
            infoText={
              isAngelSnapshot
                ? "Total buy and sell quantities, open interest, and traded volume."
                : "Total and top-of-book buy/sell quantities plus open interest."
            }
            rows={[
              { k: "Tot Buy", v: fi(marketSnapshot.totalBuyQuantity), tone: "pos" },
              { k: "Tot Sell", v: fi(marketSnapshot.totalSellQuantity), tone: "neg" },
              ...(isAngelSnapshot
                ? []
                : [
                    {
                      k: "Bid Qty",
                      v: fi(marketSnapshot.topBuyQuantity),
                      tone: "pos",
                    },
                    {
                      k: "Ask Qty",
                      v: fi(marketSnapshot.topSellQuantity),
                      tone: "neg",
                    },
                  ]),
              { k: "OI", v: fi(marketSnapshot.openInterest) },
              ...(isAngelSnapshot
                ? [{ k: "Vol", v: fi(marketSnapshot.volume) }]
                : []),
            ]}
          />
          <DenseTable
            title="Last Trade"
            infoText={
              isAngelSnapshot
                ? "Most recent trade quantity, time, exchange, and symbol token."
                : "Most recent trade quantity, time, exchange, segment, and volume."
            }
            rows={[
              { k: "Qty", v: fi(marketSnapshot.lastQuantity) },
              { k: "Time", v: marketSnapshot.formatLastTradeTime() },
              { k: "Exch", v: marketSnapshot.exchange || "-" },
              {
                k: isAngelSnapshot ? "Token" : "Seg",
                v: isAngelSnapshot
                  ? String(marketSnapshot.scripCode || "-")
                  : marketSnapshot.exchangeType || "-",
              },
              ...(isAngelSnapshot
                ? []
                : [{ k: "Vol", v: fi(marketSnapshot.volume) }]),
            ]}
          />
        </div>
      </div>

      <style>
        {`
          @media (max-width: 900px) {
            .smp-mid-row, .smp-bottom-row {
              grid-template-columns: 1fr !important;
            }
          }
          @media (max-width: 640px) {
            .smp-kpi-grid { grid-template-columns: 1fr 1fr !important; }
          }
        `}
      </style>
    </div>
  );
}
